import { appColors } from '../Config/Theme';

// ── Counts ──

export interface NeedsAttentionCounts {
  total: number;
  accounted: number;
  notAccounted: number;
  notChecked: number;
}

// ── Page state ──

export type NeedsAttentionPageState =
  | 'closed'
  | 'emptyRoster'
  | 'needsAttention'
  | 'complete'
  | 'notStarted'
  | 'inProgressNoAttention';

/**
 * Derive the page state for the Needs Attention page.
 * Priority: closed > emptyRoster > needsAttention > complete > notStarted > inProgressNoAttention
 */
export const getNeedsAttentionPageState = (
  counts: NeedsAttentionCounts,
  incidentStatus?: string,
): NeedsAttentionPageState => {
  if (
    incidentStatus &&
    ['closed', 'completed', 'complete', 'inactive'].includes(incidentStatus.toLowerCase().trim())
  ) {
    return 'closed';
  }
  if (counts.total === 0) return 'emptyRoster';
  if (counts.notAccounted > 0) return 'needsAttention';
  if (counts.notChecked === 0 && counts.notAccounted === 0 && counts.accounted === counts.total) return 'complete';
  if (counts.accounted === 0 && counts.notAccounted === 0 && counts.notChecked === counts.total) return 'notStarted';
  return 'inProgressNoAttention';
};

// ── "All accounted" guard ──

export const isAllAccounted = (counts: NeedsAttentionCounts): boolean =>
  counts.total > 0 &&
  counts.accounted === counts.total &&
  counts.notChecked === 0 &&
  counts.notAccounted === 0;

// ── Empty-state content ──

export interface EmptyStateContent {
  title: string;
  body: string;
}

export const getNeedsAttentionEmptyState = (
  state: NeedsAttentionPageState,
  counts: NeedsAttentionCounts,
): EmptyStateContent => {
  switch (state) {
    case 'notStarted':
      return {
        title: 'Headcount has not started',
        body: `No one has been marked Not Accounted yet. ${counts.notChecked} ${counts.notChecked === 1 ? 'person' : 'people'} still need to be checked.`,
      };
    case 'inProgressNoAttention':
      return {
        title: 'No one is marked Not Accounted',
        body: `${counts.notChecked} ${counts.notChecked === 1 ? 'person' : 'people'} still need to be checked before headcount is complete.`,
      };
    case 'complete':
      return {
        title: 'All accounted for',
        body: 'Everyone in the roster has been accounted for.',
      };
    case 'emptyRoster':
      return {
        title: 'Roster not available',
        body: 'No people are currently available in the incident roster.',
      };
    case 'closed':
      return {
        title: 'Incident closed',
        body: 'This incident has been closed. Review the final accountability report.',
      };
    case 'needsAttention':
      return {
        title: `${counts.notAccounted} ${counts.notAccounted === 1 ? 'person requires' : 'people require'} attention`,
        body: 'Review people marked Not Accounted and continue checking the remaining roster.',
      };
  }
};

// ── Quick actions ──

export interface QuickAction {
  label: string;
  route: string;
  variant: 'contained' | 'outlined';
  highlight?: boolean;
}

export const getNeedsAttentionQuickActions = (
  state: NeedsAttentionPageState,
  counts: NeedsAttentionCounts,
  incidentId: string,
): QuickAction[] => {
  switch (state) {
    case 'notStarted':
      return [
        { label: 'Start Headcount', route: `/incidents/${incidentId}/headcount`, variant: 'contained' },
        { label: 'View Not Checked', route: `/incidents/${incidentId}/headcount?status=notChecked`, variant: 'outlined' },
        { label: 'Open Full Roster', route: `/incidents/${incidentId}/headcount`, variant: 'outlined' },
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'outlined', highlight: true },
      ];
    case 'inProgressNoAttention':
      return [
        { label: 'Continue Headcount', route: `/incidents/${incidentId}/headcount`, variant: 'contained' },
        { label: 'View Not Checked', route: `/incidents/${incidentId}/headcount?status=notChecked`, variant: 'outlined' },
        { label: 'Open Full Roster', route: `/incidents/${incidentId}/headcount`, variant: 'outlined' },
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'outlined', highlight: true },
      ];
    case 'needsAttention':
      return [
        { label: 'Continue Headcount', route: `/incidents/${incidentId}/headcount`, variant: 'contained' },
        { label: 'View by Floor', route: `/incidents/${incidentId}/views?group=floor`, variant: 'outlined' },
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'outlined', highlight: true },
      ];
    case 'complete':
      return [
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'contained', highlight: true },
      ];
    case 'closed':
      return [
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'contained', highlight: true },
        { label: 'Open Read-Only Roster', route: `/incidents/${incidentId}/headcount`, variant: 'outlined' },
      ];
    case 'emptyRoster':
      return [
        { label: 'Back to Dashboard', route: `/incidents/${incidentId}/incidentdashboard`, variant: 'contained', highlight: true },
      ];
  }
};

// ── Summary card color ──

export type MetricType = 'needsAttention' | 'notChecked' | 'accounted' | 'total';

export const getMetricColor = (metric: MetricType, value: number): string => {
  switch (metric) {
    case 'needsAttention':
      return value > 0 ? appColors.status.error : appColors.text.caption;
    case 'notChecked':
      return value > 0 ? appColors.status.warning : appColors.text.caption;
    case 'accounted':
      return appColors.status.success;
    case 'total':
      return appColors.primary.main;
  }
};
